import { useEffect, useState, useRef } from "react";
import { CheckCircle } from "lucide-react";

const NAMES = ["Alex", "Maria", "Jordan", "Sam", "Lena", "Chris", "Yuki", "Carlos", "Nina", "Tom", "Zara", "Mike", "Sofia", "Luis", "Emma", "Kai", "Priya", "Omar", "Mia", "Jake"];
const COUNTRIES = ["Germany", "USA", "Brazil", "France", "Spain", "Japan", "Mexico", "Italy", "Poland", "UK", "Canada", "Turkey", "Netherlands", "Portugal", "Argentina"];

function seededRandom(seed) {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
    return () => { h ^= h << 13; h ^= h >> 17; h ^= h << 5; return (h >>> 0) / 4294967296; };
}

function getNotification(games, index) {
    const seed = `toast_${index}_${Math.floor(Date.now() / 60000)}`;
    const rng = seededRandom(seed);
    const name = NAMES[Math.floor(rng() * NAMES.length)];
    const country = COUNTRIES[Math.floor(rng() * COUNTRIES.length)];
    const game = games.length > 0 ? games[Math.floor(rng() * games.length)] : { name: "a game" };
    return { name, country, gameName: game.name };
}

const TOAST_DURATION = 4000;

const DownloadToast = ({ games = [] }) => {
    const [visible, setVisible] = useState(false);
    const [exiting, setExiting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [progress, setProgress] = useState(100);
    const indexRef = useRef(0);
    const mainTimerRef = useRef(null);
    const progressRef = useRef(null);

    const showNext = (currentGames) => {
        const notif = getNotification(currentGames, indexRef.current++);
        setNotification(notif);
        setProgress(100);
        setExiting(false);
        setVisible(true);

        const start = Date.now();
        clearInterval(progressRef.current);
        progressRef.current = setInterval(() => {
            const elapsed = Date.now() - start;
            const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
            setProgress(remaining);
            if (remaining <= 0) clearInterval(progressRef.current);
        }, 50);

        setTimeout(() => {
            setExiting(true);
            setTimeout(() => setVisible(false), 400);
        }, TOAST_DURATION);
    };

    useEffect(() => {
        if (games.length === 0) return;

        mainTimerRef.current = setTimeout(() => {
            showNext(games);

            const scheduleNext = (g) => {
                const delay = 3000 + Math.random() * 12000;
                mainTimerRef.current = setTimeout(() => {
                    showNext(g);
                    scheduleNext(g);
                }, delay);
            };
            scheduleNext(games);
        }, 3000);

        return () => {
            clearTimeout(mainTimerRef.current);
            clearInterval(progressRef.current);
        };
    }, [games.length]);

    if (!visible || !notification) return null;

    return (
        <>
            <style>{`
                @keyframes toastIn {
                    from { transform: translateX(-110%); opacity: 0; }
                    to   { transform: translateX(0);     opacity: 1; }
                }
                @keyframes toastOut {
                    from { transform: translateX(0);     opacity: 1; }
                    to   { transform: translateX(-110%); opacity: 0; }
                }
            `}</style>
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-md z-50 pointer-events-none px-3 flex justify-start">
            <div
                style={{
                    width: "272px",
                    animation: exiting
                        ? "toastOut 0.4s cubic-bezier(0.4,0,1,1) forwards"
                        : "toastIn 0.45s cubic-bezier(0,0,0.2,1) forwards",
                }}
            >
                <div
                    className="relative overflow-hidden"
                    style={{
                        background: "linear-gradient(135deg, rgba(1,15,30,0.97) 0%, rgba(1,42,74,0.95) 100%)",
                        border: "1px solid rgba(34,197,94,0.2)",
                        borderRadius: "14px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.03) inset",
                        backdropFilter: "blur(16px)",
                    }}
                >
                    {/* Top edge glow */}
                    <div
                        className="absolute top-0 left-4 right-4 h-px pointer-events-none"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.5), transparent)" }}
                    />

                    <div className="flex items-center gap-3 px-3 py-2.5">
                        {/* Icon */}
                        <div
                            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                            style={{
                                background: "linear-gradient(135deg, rgba(22,163,74,0.2), rgba(34,197,94,0.12))",
                                border: "1px solid rgba(34,197,94,0.4)",
                                boxShadow: "0 0 14px rgba(34,197,94,0.18)",
                            }}
                        >
                            <CheckCircle className="w-4 h-4" style={{ color: "#22c55e" }} />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                            <div className="semiBold leading-tight" style={{ fontSize: "13px", color: "#fff" }}>
                                From {notification.country}
                            </div>
                            <div className="medium leading-snug" style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
                                just downloaded{" "}
                                <span style={{ color: "#89d4f5" }} className="truncate">
                                    {notification.gameName}
                                </span>{" "}
                                ✓
                            </div>
                        </div>
                    </div>

                    {/* Drain progress bar */}
                    <div className="absolute bottom-0 left-0 right-0" style={{ height: "2px", background: "rgba(255,255,255,0.05)" }}>
                        <div
                            style={{
                                height: "100%",
                                width: `${progress}%`,
                                background: "linear-gradient(90deg, #16a34a, #22c55e)",
                                transition: "width 0.05s linear",
                                boxShadow: "0 0 6px rgba(34,197,94,0.7)",
                            }}
                        />
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};

export default DownloadToast;
